import { createSelector, createSelectorCreator } from 'reselect';
import { initialState } from './reducer';
import moment from 'moment'
import CommonFunctionsUtils from '../../utils/CommonFunctionsUtils'
import EntityUtils from '../../utils/EntityUtils'

/**
 * Direct selector to the app state domain
 */

const selectAppDomain = state => state.app || initialState;
const selectRouteDomain = state => state.router;
/**
 * Default selector used by App
 */

const makeSelectApp = () => createSelector(
  selectAppDomain,
  (substate) => substate
);

/**
 * Other specific selectors
 */

const selectAuthUser = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.authUser;
  }
);

const selectLoadingProfileAuthUser = () => createSelector(
  makeSelectApp(),
  (app) => {
    let user = app.loadingProfileAuthUser
    const params = new URLSearchParams(window.location.search);
    if (params && params.get("accountId")) {
      user = {...app.loadingProfileAuthUser};
      user.uid = params.get("accountId");
    }
    return user;
  }
);


const selectCompany = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.company;
  }
);

const selectIsCompanyUpdating = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.updatingCompany;
  }
);

const selectIntegrations = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.integrations;
  }
);

const selectPlan = () => createSelector(
  makeSelectApp(),
  (app) => {
    let plan = app.plan
    try{plan.businessName = app.company.properties.businessName}catch(e){}
    return plan;
  }
);

const selectCustomers = () => createSelector(
  makeSelectApp(),
  (app) => {
    let customersArray = CommonFunctionsUtils.convertMapToArray(app.customers)
    customersArray.sort(function (a, b) {
      let aName = EntityUtils.getCustomerName(a).toLowerCase().trim()
      let bName = EntityUtils.getCustomerName(b).toLowerCase().trim()
      if (aName < bName)
        return -1;
      if (aName > bName)
        return 1;
      return 0;
    });
    return customersArray;
  }
);
const selectCustomersMap = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.customers;
  }
);

const selectActiveCustomers = () => createSelector(
  makeSelectApp(),
  (app) => {
    let customersArray = CommonFunctionsUtils.convertMapToArray(app.customers).filter(customer => {
      return customer.active
    })
    customersArray.sort(function (a, b) {
      let aName = EntityUtils.getCustomerName(a)
      let bName = EntityUtils.getCustomerName(b)
      if (aName < bName)
        return -1;
      if (aName > bName)
        return 1;
      return 0;
    });
    return customersArray;
  }
);

const selectContractors = () => createSelector(
  makeSelectApp(),
  (app) => {
    let contractorsArray = CommonFunctionsUtils.convertMapToArray(app.contractors)
    contractorsArray.sort(function (a, b) {
      let aName = EntityUtils.getCustomerName(a)
      let bName = EntityUtils.getCustomerName(b)
      if (aName < bName)
        return -1;
      if (aName > bName)
        return 1;
      return 0;
    });
    return contractorsArray;
  }
);

const selectHaulers = () => createSelector(
  makeSelectApp(),
  (app) => {
    let haulersArray = []
    let contractorsArray = CommonFunctionsUtils.convertMapToArray(app.contractors)
    for(let key in contractorsArray){
      let contractor = contractorsArray[key]
      if(contractor.type === 'Hauler'){
        haulersArray.push(contractor)
      }
    }
    haulersArray.sort(function (a, b) {
      let aName = EntityUtils.getCustomerName(a)
      let bName = EntityUtils.getCustomerName(b)
      if (aName < bName)
        return -1;
      if (aName > bName)
        return 1;
      return 0;
    });
    return haulersArray;
  }
);

const selectContractorsMap = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.contractors
  }
);

const selectLoadingContractors = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.loadingContractors;
  }
);

const selectNotificationProperties = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.notificationProperties;
  }
);

const selectProfile = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.profile
  }
);

const selectLoggedInTeamMember = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.memberProfile
  }
);

const selectMemberProfile = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.memberProfile;
  }
);

const selectTeam = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.selectedCompany;
  }
);

const selectMessages = () => createSelector(
  makeSelectApp(),
  (app) => {
    let messagesArray = CommonFunctionsUtils.convertMapToArray(app.messages)
    messagesArray.sort(function (a, b) {
      if (a.date < b.date)
        return 1;
      if (a.date > b.date)
        return -1;
      return 0;
    });
    messagesArray = messagesArray.filter((m) => {
      return m.message.indexOf('Welcome to Docket Chat!') === -1
    })
    return messagesArray;
  }
);

const selectInvitation = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.invitation;
  }
);

const selectPayments = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.payments;
  }
);

const selectInvoices = () => createSelector(
  makeSelectApp(),
  (app) => {
    if (app.invoices === null) return undefined
    return app.invoices
  }
);

const selectUnpaidInvoices = () => createSelector(
  makeSelectApp(), selectPayments(),
  (app, payments) => {
    if (app.unpaidInvoices === null) return []

    let sortedInvoices = []
    for(let key in app.unpaidInvoices){
      let inv = app.unpaidInvoices[key]
      if(!inv.badDebt && !inv.voided){
        sortedInvoices.push(inv)
      }
    }

    sortedInvoices.sort(function (a, b) {
      if (a.dueDate < b.dueDate)
        return -1;
      if (a.dueDate > b.dueDate)
        return 1;
      return 0;
    });
    
    return sortedInvoices
  }
);

const selectUserConfigs = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.userConfigs;
  }
);

const selectPermissions = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.permissions;
  }
);

const selectccGateway = () => createSelector(
  makeSelectApp(),
  (app) => {    
    return app.ccGateway ? app.ccGateway : {};
  }
);

const selectIsSubscribing = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.isSubscribing;
  }
);

const selectSubscriptionErrorType = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.subscriptionErrorType;
  }
);

const selectDocuments = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.documents;
  }
);

const selectQBIsSynching = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.qbSynching;
  }
);

const selectCoupons = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.coupons;
  }
);

const selectProductName = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.productName;
  }
);

const selectCompanyProducts = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.products
  }
)

const selectCompanyDiscounts = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.discounts ? app.discounts : {}
  }
)

const selectWorkflows = () => createSelector(
  makeSelectApp(),
  app => {
    if (app.workflows) {
      return app.workflows;
    } else {
      return {}
    }
  }
)

const selectCompanyVariants = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.variants
  }
)

const selectConnectedAccounts = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.connectedAccounts;
  }
);

const selectConnectedCompanies = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.connectedCompanies;
  }
);

const selectHistory = () => createSelector(
  makeSelectApp(),
  (app) => {
    return app.history
  }
)

const selectCartConfigurations = () => createSelector(
  makeSelectApp(),
  app => app.cartConfigurations
)

const selectEmbedConfigurations = () => createSelector(
  makeSelectApp(),
  app => app.embedConfigurations
)

const selectApiKeyMap = () => createSelector(
  makeSelectApp(),
  app => app.apiMap
)

const selectPermissionId = () => createSelector(
  makeSelectApp(),
  (app) => {
    let permissionId = undefined
    if( app.members !== undefined && app.profile !== undefined){
      let memberId = app.profile.company[app.profile.selectedCompany].memberId;
      permissionId = app.members[memberId].permissionId
      // availablePages = CommonFunctionsUtils.getAvailablePages(permissionId, this.props.permissions)
    }

    return permissionId;
  }
);

export default {
  selectAppDomain,
  selectAuthUser,
  makeSelectApp,
  selectCustomers,
  selectCustomersMap,
  selectActiveCustomers,
  selectCompany,
  selectProfile,
  selectLoggedInTeamMember,
  selectTeam,
  selectInvitation,
  selectInvoices,
  selectLoadingProfileAuthUser,
  selectPermissions,
  selectMemberProfile,
  selectPlan,
  selectNotificationProperties,
  selectIsSubscribing,
  selectSubscriptionErrorType,
  selectIntegrations,
  selectUserConfigs,
  selectUnpaidInvoices,
  selectccGateway,
  selectPermissionId,
  selectIsCompanyUpdating,
  selectDocuments,
  selectCoupons,
  selectQBIsSynching,
  selectProductName,
  selectCompanyProducts,
  selectCompanyDiscounts,
  selectCompanyVariants,
  selectHistory,
  selectWorkflows,
  selectConnectedAccounts,
  selectConnectedCompanies,
  selectCartConfigurations,
  selectEmbedConfigurations,
  selectApiKeyMap,
};
